import { ActivationMark } from '@/shared/ui/ActivationMark'
import { Button } from '@/shared/ui/Button'
import { formatMonthlyPrice } from '@/shared/lib/format'

import type { ModuleDefinition } from './registry'
import { getModule } from './registry'
import { isAddonModule } from './module-keys'
import { useModuleActivation } from './useModules'

export interface ModuleLockedProps {
  module: ModuleDefinition
}

/**
 * Cosa si vede al posto di un modulo spento.
 *
 * Non un vicolo cieco né un rimbalzo altrove: la pagina resta dov'è e spiega
 * cosa contiene, quanto costa e cosa aggiungerebbe agli altri moduli. Chi
 * decide, decide qui.
 */
export function ModuleLocked({ module }: ModuleLockedProps) {
  const { setActive, pendingKey, isActive } = useModuleActivation()
  // Legato a una const: il restringimento di tipo sopravvive dentro le closure
  // dei gestori, cosa che l'accesso a `module.key` non farebbe.
  const key = module.key
  const isPending = pendingKey === key

  return (
    <div className="flex flex-1 items-start justify-center p-6 sm:p-10">
      <div className="bg-ink-800 flex w-full max-w-xl overflow-hidden rounded-lg border border-[var(--hairline)]">
        <ActivationMark active={false} className="self-stretch" />

        <div className="flex flex-col gap-5 p-6">
          <div>
            <p className="type-eyebrow mb-2">Modulo non attivo</p>
            <h1 className="type-display">{module.name}</h1>
            <p className="text-thread-400 text-sm mt-2">{module.summary}</p>
          </div>

          {module.affects && module.affects.length > 0 ? (
            <div className="border-t border-[var(--hairline)] pt-4">
              <p className="type-eyebrow mb-2">Cosa aggiunge altrove</p>
              <ul className="text-thread-400 text-sm flex flex-col gap-1.5">
                {module.affects.map((affect) => (
                  <li key={`${affect.module}-${affect.what}`} className="flex gap-2">
                    <span aria-hidden="true" className="text-brass-500">
                      —
                    </span>
                    <span>
                      In <span className="text-linen-200">{getModule(affect.module).name}</span>,{' '}
                      {affect.what}.
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--hairline)] pt-4">
            {module.price !== undefined ? (
              <p className="type-data text-brass-500">{formatMonthlyPrice(module.price)}</p>
            ) : (
              <span />
            )}

            {isAddonModule(key) && !isActive(key) ? (
              <Button
                variant="primary"
                pending={isPending}
                pendingLabel="Attivazione…"
                onClick={() => setActive(key, true)}
              >
                Attiva {module.name}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
